import { USE_SAMPLE, WEB_API_BASE } from "@/config";
import type { OrgSlugParams } from "@/lib/route-schemas";
import { orgSlugParamsSchema } from "@/lib/route-schemas";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useTheme } from "@repo/forms-ui";
import type React from "react";

type PortalBlock =
	| { type: "hero"; heading?: string; subheading?: string }
	| { type: "links"; items?: Array<{ label?: string; href: string }> }
	| Record<string, unknown>;

interface PortalConfig {
	title?: string;
	theme?: { accent?: string; mode?: string };
	blocks?: PortalBlock[];
}

const SAMPLE_PORTAL: PortalConfig = {
	title: "Sample Client Portal",
	theme: { accent: "blue", mode: "light" },
	blocks: [
		{
			type: "hero",
			heading: "Welcome to Your Portal",
			subheading: "Access your forms and documents",
		},
		{
			type: "links",
			items: [
				{ label: "Start Intake Form", href: "/forms/acme/intake" },
				{ label: "Upload Documents", href: "/forms/acme/uploads" },
			],
		},
	],
};

export default function PortalRoute() {
	const params = useParams({ strict: false }) as unknown as OrgSlugParams;
	const parse = orgSlugParamsSchema.safeParse(params);
	const { org, slug } = parse.success
		? parse.data
		: { org: "invalid", slug: "invalid" };

	const { data, isLoading, error } = useQuery({
		queryKey: ["portal-config", org, slug],
		queryFn: async () => {
			if (USE_SAMPLE) return SAMPLE_PORTAL;
			const res = await fetch(
				`${WEB_API_BASE.replace(/\/$/, "")}/api/portal-config/${encodeURIComponent(org)}/${encodeURIComponent(slug)}`,
				{ headers: { Accept: "application/json" } },
			);
			if (!res.ok) throw new Error(`Failed to load portal: ${res.status}`);
			return (await res.json()) as Record<string, unknown>;
		},
	});

	const { mode, setMode, accent, setAccent } = useTheme();
	const onAccentChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
		const v = e.target.value as "blue" | "violet" | "green" | "orange";
		setAccent(v);
	};

	if (!parse.success) {
		return (
			<main className="container">
				<header className="app-header">
					<div style={{ display: "grid", gap: 6 }}>
						<span className="pill">Portal</span>
						<h1 style={{ margin: 0 }}>Invalid parameters</h1>
						<p className="muted">Expected org/slug with alphanumeric and dashes.</p>
					</div>
					<div className="toolbar">
					<button type="button" className="btn muted" onClick={() => setMode(mode === "light" ? "dark" : "light")}>{mode === "light" ? "☾ Dark" : "☀︎ Light"}</button>
					<select value={accent} onChange={onAccentChange} className="btn ghost" aria-label="Accent">
							<option value="blue">Blue</option>
							<option value="violet">Violet</option>
							<option value="green">Green</option>
							<option value="orange">Orange</option>
						</select>
					</div>
				</header>
				<section className="card" style={{ marginTop: 12 }}>
					<div className="card-body">
						<Link to="/" className="btn">Go Home</Link>
					</div>
				</section>
			</main>
		);
	}

	if (isLoading)
		return (
			<main className="container">
				<p className="muted">Loading portal…</p>
			</main>
		);
	if (error)
		return (
			<main className="container">
				<section className="card" style={{ borderColor: "#ef4444" }}>
					<div className="card-body">
						<div style={{ display: "grid", gap: 8 }}>
							<strong style={{ color: "#ef4444" }}>Failed to load portal</strong>
							<span className="muted">{(error as Error).message}</span>
						<button type="button" className="btn" onClick={() => window.location.reload()}>Retry</button>
						</div>
					</div>
				</section>
			</main>
		);

	const portal = (data ?? SAMPLE_PORTAL) as PortalConfig;

	return (
		<main className="container">
			<header className="app-header">
				<div style={{ display: "grid", gap: 6 }}>
					<span className="pill">{org}</span>
					<h1 style={{ margin: 0 }}>{portal.title ?? `${org} / ${slug}`}</h1>
					<p className="muted">{slug}</p>
				</div>
				<div className="toolbar">
					<button type="button" className="btn muted" onClick={() => setMode(mode === "light" ? "dark" : "light")}>{mode === "light" ? "☾ Dark" : "☀︎ Light"}</button>
					<select value={accent} onChange={onAccentChange} className="btn ghost" aria-label="Accent">
						<option value="blue">Blue</option>
						<option value="violet">Violet</option>
						<option value="green">Green</option>
						<option value="orange">Orange</option>
					</select>
					<Link to="/" className="btn ghost">Home</Link>
				</div>
			</header>

			<section className="grid" style={{ gap: 18, marginTop: 8 }}>
				{Array.isArray(portal.blocks) &&
					portal.blocks.map((b) => <Block key={blockKey(b)} block={b} />)}
			</section>
		</main>
	);
}

function blockKey(block: PortalBlock): string {
	if (!block || typeof block !== "object") return "unknown";
	if ((block as { type?: string }).type === "hero") {
		return `hero:${(block as { heading?: string }).heading ?? ""}`;
	}
	if ((block as { type?: string }).type === "links") {
		const items = (block as { items?: Array<{ href: string }> }).items ?? [];
		return `links:${items.map((i) => i.href).join("|")}`;
	}
	return `block:${JSON.stringify(block)}`;
}

function Block({ block }: { block: PortalBlock }) {
  if (!block || typeof block !== "object") return null;
  const kind = (block as { type?: string }).type;
  switch (kind) {
    case "hero": {
      const b = block as Extract<PortalBlock, { type: "hero" }>;
      return (
        <article className="hero">
          <div className="hero-inner">
            <h2 style={{ margin: 0 }}>{b.heading ?? "Welcome"}</h2>
            {b.subheading ? (
              <p style={{ marginTop: 6 }}>{b.subheading}</p>
            ) : null}
          </div>
        </article>
      );
    }
    case "links": {
      const items = (block as { items?: Array<{ label?: string; href: string }> }).items ?? [];
      return (
        <article className="card">
          <div className="card-header">
            <div className="card-title">Links</div>
            <div className="card-desc">Quick access</div>
          </div>
          <div className="card-body" style={{ display: "grid", gap: 10 }}>
            {items.map((it) => (
              <a key={it.href} href={it.href} className="btn ghost" style={{ justifyContent: "space-between" }}>
                <span>{it.label ?? it.href}</span>
                <span aria-hidden>↗</span>
              </a>
            ))}
          </div>
        </article>
      );
    }
    default:
      return (
        <article className="card">
          <div className="card-body">
            <pre style={{ fontSize: 12, opacity: 0.8, margin: 0 }}>
              {JSON.stringify(block, null, 2)}
            </pre>
          </div>
        </article>
      );
  }
}
